import React, { useState, useEffect, useCallback } from 'react';
import { adminApiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DataTable } from './common/DataTable';
import {
  DollarSign,
  Search,
  Filter,
  CheckCircle,
  Eye,
  RefreshCw,
  Gift
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ColumnDef } from '@tanstack/react-table';

interface TempleDonation {
  _id: string;
  id: string;
  donor: {
    _id: string;
    name: string;
    email: string;
  };
  donorName?: string;
  donorEmail?: string;
  amount: number;
  currency: string;
  type: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  receiptNumber: string;
  createdAt: string;
}

const TempleDonationsPage: React.FC = () => {
  const { user } = useAuth();
  const [donations, setDonations] = useState<TempleDonation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchDonations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApiClient.getDonations({
        templeId: user?.templeId,
        limit: 1000,
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.type !== 'all' && { type: filters.type })
      });

      if (response.success && response.data) {
        setDonations(response.data.donations.map((d: any) => ({
          ...d,
          id: d._id || d.id
        })));
      } else {
        toast.error(response.message || 'Failed to fetch donations');
      }
    } catch (err: any) {
      toast.error('Error fetching donations');
    } finally {
      setLoading(false);
    }
  }, [user?.templeId, filters]);

  useEffect(() => {
    if (user?.templeId) {
      fetchDonations();
    }
  }, [user?.templeId, fetchDonations]);

  const handleStatusUpdate = async (id: string, newStatus: string) => {
    const loadingToast = toast.loading('Updating status...');
    try {
      const response = await adminApiClient.updateDonationStatus(id, newStatus);
      if (response.success) {
        toast.success('Status updated', { id: loadingToast });
        fetchDonations();
      } else {
        toast.error(response.message || 'Failed', { id: loadingToast });
      }
    } catch (err) {
      toast.error('Error updating status', { id: loadingToast });
    }
  };

  const columns: ColumnDef<TempleDonation>[] = [
    {
      accessorKey: 'receiptNumber',
      header: 'Receipt #',
      cell: (info) => <span className="font-mono text-gray-600 text-sm">#{info.getValue<string>()}</span>
    },
    {
      accessorKey: 'donor',
      header: 'Donor',
      cell: (info) => (
        <div>
          <div className="font-medium text-gray-900">{info.row.original.donorName || info.row.original.donor?.name || 'Anonymous'}</div>
          <div className="text-xs text-gray-500">{info.row.original.donorEmail || info.row.original.donor?.email}</div>
        </div>
      )
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: (info) => (
        <div className="font-bold text-gray-900 flex items-center gap-1">
          <DollarSign size={14} className="text-gray-400" />
          {info.getValue<number>().toLocaleString()}
          <span className="text-xs text-gray-500 font-normal">{info.row.original.currency}</span>
        </div>
      )
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: (info) => (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-700 capitalize">
          <Gift size={12} /> {info.getValue<string>()}
        </span>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => {
        const s = info.getValue<string>();
        const colors: any = { completed: 'text-green-700 bg-green-50', pending: 'text-yellow-700 bg-yellow-50', failed: 'text-red-700 bg-red-50' };
        return <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${colors[s] || 'bg-gray-100'} `}>{s}</span>;
      }
    },
    {
      id: 'created_at',
      header: 'Date',
      cell: (info) => <span className="text-sm text-gray-500">{new Date(info.row.original.createdAt).toLocaleDateString()}</span>
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <div className="flex gap-2">
          {info.row.original.status === 'pending' && (
            <button onClick={() => handleStatusUpdate(info.row.original.id, 'completed')} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Approve">
              <CheckCircle size={18} />
            </button>
          )}
          <button className="p-1 text-gray-500 hover:bg-gray-50 rounded" title="View Details">
            <Eye size={18} />
          </button>
        </div>
      )
    }
  ];

  const filtered = donations.filter(d =>
    (d.donorName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.receiptNumber || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Temple Donations</h1>
          <p className="text-sm text-gray-500">Track and manage incoming donations</p>
        </div>
        <button onClick={fetchDonations} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-500 mb-1">Total Collected</p>
          <p className="text-2xl font-bold text-gray-900">${donations.reduce((acc, c) => acc + c.amount, 0).toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-500 mb-1">Transactions</p>
          <p className="text-2xl font-bold text-gray-900">{donations.length}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-500 mb-1">Pending Approval</p>
          <p className="text-2xl font-bold text-orange-600">{donations.filter(d => d.status === 'pending').length}</p>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 space-y-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by donor or receipt..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none"
            />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${showFilters ? 'bg-orange-50 border-orange-200 text-orange-700' : ''}`}>
            <Filter size={18} /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t">
            <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} className="px-3 py-2 border rounded-lg outline-none">
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </select>
            <select value={filters.type} onChange={e => setFilters({ ...filters, type: e.target.value })} className="px-3 py-2 border rounded-lg outline-none">
              <option value="all">All Types</option>
              <option value="one-time">One-Time</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <DataTable
          data={filtered}
          columns={columns}
          loading={loading}
          pagination={{ pageIndex: 0, pageSize: 10 }}
          onPaginationChange={() => { }}
          onSortingChange={() => { }}
          sorting={[]}
          pageCount={Math.ceil(filtered.length / 10)}
        />
      </div>
    </div>
  );
};

export default TempleDonationsPage;
